import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { getStudentDiscountByYear } from "../../../api/studentPaiement";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import withPermission from "../../../core/common/autorisations/withPermission";

type Props = {
  student?: any;
  plans: any[];
  checkedPlans: string[];
  setCheckedPlans: (value: string[] | ((prev: string[]) => string[])) => void;
  onAddPayment: () => void;
  currency?: string;
};

const stickyFooterCellStyle = {
  position: "sticky" as const,
  bottom: 0,
  backgroundColor: "#fff",
  zIndex: 2,
  boxShadow: "0 -1px 0 #dee2e6",
};

// Boutton protéger pour ajouter un nouveau paiement
const ProtectedBtnAddPayment = withPermission(
  (props: any) => (
    <button
      className="btn btn-outline-primary btn-sm"
      onClick={props.onAddPayment}
      disabled={props.checkedPlans.length === 0}
    >
      Ajouter un paiement
    </button>
  ),
  [buildPermission(Resource.PAYMENT, Action.CREATE)],
  "masquer",
);

const StudentPaymentPlansTable = ({
  student,
  plans,
  checkedPlans,
  setCheckedPlans,
  onAddPayment,
  currency = "FCFA",
}: Props) => {
  const { selectedYear } = useAcademicYear();
  const [discountMaps, setDiscountMaps] = useState<{
    byStudentPlanId: Record<string, number>;
    byPaymentPlanId: Record<string, number>;
  }>({ byStudentPlanId: {}, byPaymentPlanId: {} });

  const getPlanAmount = (plan: any) =>
    Number(plan?.PaymentPlan?.amount ?? plan?.amount ?? 0);

  const getPlanTemplateId = (plan: any) =>
    String(plan?.id_paymentPlans || plan?.PaymentPlan?.id || "");

  const getDiscountAmountByPlan = (plan: any) => {
    const studentPlanId = String(plan?.id || "");
    const templatePlanId = getPlanTemplateId(plan);
    const byStudent = Number(discountMaps.byStudentPlanId[studentPlanId] || 0);
    const byTemplate = Number(
      discountMaps.byPaymentPlanId[templatePlanId] || 0,
    );
    return byStudent > 0 ? byStudent : byTemplate;
  };

  const getNetPlanAmount = (plan: any) =>
    Math.max(0, getPlanAmount(plan) - getDiscountAmountByPlan(plan));

  const getRemainingByPlan = (plan: any) =>
    Math.max(0, getNetPlanAmount(plan) - Number(plan?.paid || 0));

  const fetchStudentDiscounts = async () => {
    try {
      if (!selectedYear?.id || !student?.id) {
        setDiscountMaps({ byStudentPlanId: {}, byPaymentPlanId: {} });
        return;
      }

      const res = await getStudentDiscountByYear(
        student.id,
        selectedYear.id,
        buildPermissionContext(Resource.PAYMENT, Action.READ),
      );
      const byStudentPlanId: Record<string, number> = {};
      const byPaymentPlanId: Record<string, number> = {};

      const discounts = res?.data?.discount || [];
      discounts.forEach((discount: any) => {
        const repartitions = discount?.RepartitionStudentDiscounts || [];
        repartitions.forEach((rep: any) => {
          const repAmount = Number(rep?.amount || 0);
          const studentPlanId = String(
            rep?.id_studentPayementPlans || "",
          );
          const paymentPlanId = String(
            rep?.StudentPayementPlan?.PaymentPlan?.id || "",
          );

          if (studentPlanId) {
            byStudentPlanId[studentPlanId] =
              Number(byStudentPlanId[studentPlanId] || 0) + repAmount;
          }
          if (paymentPlanId) {
            byPaymentPlanId[paymentPlanId] =
              Number(byPaymentPlanId[paymentPlanId] || 0) + repAmount;
          }
        });
      });

      setDiscountMaps({ byStudentPlanId, byPaymentPlanId });
    } catch (error: any) {
      setDiscountMaps({ byStudentPlanId: {}, byPaymentPlanId: {} });
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Impossible de charger les bourses/remises de l'etudiant",
      );
    }
  };

  useEffect(() => {
    fetchStudentDiscounts();
  }, [selectedYear?.id, student?.id]);

  const selectablePlanIds = useMemo(
    () =>
      plans
        .filter((plan) => getRemainingByPlan(plan) > 0)
        .map((plan) => plan.id),
    [plans, discountMaps],
  );

  useEffect(() => {
    setCheckedPlans((prev) =>
      prev.filter((id) => selectablePlanIds.includes(id)),
    );
  }, [selectablePlanIds, setCheckedPlans]);

  const sumPlansAmount = Array.isArray(plans)
    ? plans.reduce((s, p: any) => s + getPlanAmount(p), 0)
    : 0;

  const sumPlansDiscount = Array.isArray(plans)
    ? plans.reduce((s, p: any) => s + getDiscountAmountByPlan(p), 0)
    : 0;

  const sumPlansPaid = Array.isArray(plans)
    ? plans.reduce((s, p: any) => s + Number(p?.paid || 0), 0)
    : 0;

  return (
    <div className="card mb-3">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h6 className="mb-0">Honoraires</h6>
        <div className="d-flex flex-column align-items-start gap-3">
          <div>
            Apres maturite:
            <span className="text-danger fw-semibold">
              {" "}
              {(student?.credit || 0).toLocaleString("fr-FR")} {currency}
            </span>
          </div>
          <div>
            Besoins d&apos;etre paye:
            <span className="text-primary fw-semibold">
              {" "}
              {(student?.non_paye || 0).toLocaleString("fr-FR")} {currency}
            </span>
          </div>
        </div>
      </div>
      <div className="card-body p-0">
        <div
          className="table-responsive"
          style={{ maxHeight: 520, overflowY: "auto" }}
        >
          <table className="table mb-0">
            <thead>
              <tr>
                <th className="text-nowrap">Plan de paiement</th>
                <th className="text-nowrap">Maturite</th>
                <th className="text-nowrap">Montant</th>
                <th className="text-nowrap">Montant Bourse / Remise</th>
                <th className="text-nowrap">Paye</th>
                <th className="text-end">
                  <ProtectedBtnAddPayment
                    onAddPayment={onAddPayment}
                    checkedPlans={checkedPlans}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => {
                const paid = Number(plan?.paid || 0);
                const netAmount = getNetPlanAmount(plan);
                const remaining = getRemainingByPlan(plan);
                return (
                  <tr key={plan.id}>
                    <td>{plan?.PaymentPlan?.label}</td>
                    <td>
                      {new Date(plan?.PaymentPlan?.due_date).toLocaleDateString(
                        "fr-FR",
                      )}
                    </td>
                    <td>{getPlanAmount(plan).toLocaleString("fr-FR")}</td>
                    <td className="text-primary fw-semibold">
                      {getDiscountAmountByPlan(plan).toLocaleString("fr-FR")}
                    </td>
                    <td
                      className={`text-${paid < netAmount ? "danger" : "success"} fw-semibold`}
                    >
                      {paid.toLocaleString("fr-FR")}
                    </td>
                    <td className="text-end">
                      {remaining > 0 ? (
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={checkedPlans.includes(plan.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setCheckedPlans((prev) =>
                              checked
                                ? [...prev, plan.id]
                                : prev.filter((id) => id !== plan.id),
                            );
                          }}
                        />
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="fw-semibold" style={stickyFooterCellStyle}>
                  Somme:
                </td>
                <td style={stickyFooterCellStyle}></td>
                <td style={stickyFooterCellStyle}>
                  {sumPlansAmount.toLocaleString("fr-FR")}
                </td>
                <td
                  className="text-primary fw-semibold"
                  style={stickyFooterCellStyle}
                >
                  {sumPlansDiscount.toLocaleString("fr-FR")}
                </td>
                <td
                  className="text-success fw-semibold"
                  style={stickyFooterCellStyle}
                >
                  {sumPlansPaid.toLocaleString("fr-FR")}
                </td>
                <td style={stickyFooterCellStyle}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default withPermission(
  StudentPaymentPlansTable,
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.PAYMENT, action),
    ),
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.STUDENT_PAYMENT_PLAN, action),
    ),
    // ...Object.values(Action).map((action) => buildPermission(Resource.BOURSE_REMISE,action)),
  ],
  "masquer",
);
